/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { SUPERVISORROLE, COACHING } from '../../constants';
import { selectCurrentRole, setChatAgtId } from '../../pages/login/authSlice';
import { RootState } from '../../store/store';
import { CoachingTabs } from '../../utils/typeConstants';
import UserConsentDialog from './UserConsentDialog';
import notification from '../../assets/Notification.png'
import drop from '../../assets/Drop.png'
import warning from '../../assets/warning.png'
import './persistentIframeModal.scss';


const getInitials = (name: string): string => {
    if (!name) return '??';
    return name
        .trim()
        .split(' ')
        .filter(word => word.length > 0)
        .map(word => word[0].toUpperCase())
        .slice(0, 2)
        .join('');
};

// Avatar component
const Avatar: React.FC<{ name: string; size?: number }> = ({ name, size = 80 }) => {
    const initials = getInitials(name);
    
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #7D71E2 0%, #0047FF 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: size * 0.4,
                fontWeight: 600,
                letterSpacing: '1px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
        >
            {initials}
        </div>
    );
};

interface PersistentIframeModalProps {
    src: string;
    agentDeclined?: boolean;
    agentOffline?: boolean;
    agentAccepted?: boolean;
    currentUserName?: string;
    otherUserName?: string;
}

const PersistentIframeModal: React.FC<PersistentIframeModalProps> = ({ 
    src, 
    agentDeclined, 
    agentOffline, 
    agentAccepted,
    currentUserName = 'User',
    otherUserName = 'User'
}) => {
    const role = useSelector(selectCurrentRole);
    const token = useSelector((state: RootState) => state.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [visibleIframe, setVisibleIframe] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const [showIcon, setShowIcon] = useState(true);
    const [isMinimized, setIsMinimized] = useState(false);
    const [position, setPosition] = useState({ x: 20, y: 500 });
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [visible, setVisible] = useState(true);
    const [minimizedVariant, setMinimizedVariant] = useState<'two' | 'single' | null>(null);
    const [isRecording, setIsRecording] = useState(false);
    const [isStoppingRecording, setIsStoppingRecording] = useState(false);
    const [showStartRecordingModal, setShowStartRecordingModal] = useState(false);
    const [showRecordingEndedModal, setShowRecordingEndedModal] = useState(false);
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // TODO: Validate origin in production
            // if (event.origin !== 'https://your-webrtc-domain.com') return;
            
            if (event.data.type === 'REQUEST_TOKEN') {
                console.log('📨 Iframe requested token, sending...');
                iframeRef.current?.contentWindow?.postMessage({
                    type: 'TOKEN_RESPONSE',
                    token
                }, '*');
            }
            // 👇 NEW: iframe is asking to start recording
            if (event.data.type === 'REQUEST_START_RECORDING') {
                setShowStartRecordingModal(true);
            }
            if (event.data.type === 'RECORDING_STARTED') {
                console.log('🔴 Recording started:', event.data);
                setIsRecording(true);
            }
            if (event.data.type === 'RECORDING_STOPPED') {
                console.log('⏹️ Recording stopped:', event.data);
                setIsRecording(false);
                setShowRecordingEndedModal(true);
            }
        };
        
        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [token]);

    // Prevent closing browser/tab while recording
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isRecording) {
                e.preventDefault();
                e.returnValue = 'Recording is in progress. Please stop the recording before closing.';
                return e.returnValue;
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isRecording]);

    const handleAccept = () => {
        setVisibleIframe(true);
        setIframeLoaded(true);
        setVisible(false);
    };

    const handleReject = () => {
        dispatch(setChatAgtId(null));
        setVisibleIframe(false);
        setIframeLoaded(false); // Unload iframe
        setShowIcon(false); // Hide the floating icon
        setVisible(false);
    };

    const isDragging = useRef(false);
    const offset = useRef({ x: 0, y: 0 });

    // Auto-open the iframe modal on component mount
    useEffect(() => {
        if (!iframeLoaded) {
            if (role === SUPERVISORROLE) {
                setVisible(false);
                setVisibleIframe(true);
                setIframeLoaded(true);
            } else {
                setVisible(true);
            }
        }
    }, []); // Empty dependency array means this runs once on mount

    useEffect(() => {
        if (!iframeLoaded) {
            setMinimizedVariant(null);
            return;
        }

        if (!isMinimized) {
            setMinimizedVariant(null);
            return;
        }

        // If call ended, show single variant
        if (agentDeclined || agentOffline) {
            setMinimizedVariant('single');
            return;
        }

        // If agent accepted, show two avatars
        if (agentAccepted) {
            setMinimizedVariant('two');
            return;
        }

        // Default: waiting state
        setMinimizedVariant('single');

    }, [iframeLoaded, isMinimized, agentAccepted, agentDeclined, agentOffline]);


    const handleMouseDown = (e: React.MouseEvent) => {
        isDragging.current = true;
        offset.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return;

        let newX = e.clientX - offset.current.x;
        let newY = e.clientY - offset.current.y;

        let modalWidth = 280;
        let modalHeight = 470;

        if (minimizedVariant === 'single') {
            modalHeight = 440;
        } else if (visibleIframe) {
            modalWidth = window.innerWidth * 0.6 * 0.8;
            modalHeight = window.innerHeight * 0.8 * 0.8;
        }

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        newX = Math.max(0, newX);
        newX = Math.min(viewportWidth - modalWidth, newX);
        newY = Math.max(0, newY);
        newY = Math.min(viewportHeight - modalHeight, newY);

        setPosition({
            x: newX,
            y: newY,
        });
    };

    const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const handleOpen = () => {
        console.log('Opening Persistent Iframe Modal');
        if (iframeLoaded) {
            setVisibleIframe(true);
            setIsMinimized(false);
        }
    };

    const handleMinimize = () => {
        setPosition({
            x: window.innerWidth - 300 - 20,
            y: window.innerHeight - 420 - 20,
        });
        setVisibleIframe(false);
        setIsMinimized(true);
    };

    const handleRestore = () => {
        setVisibleIframe(true);
        setIsMinimized(false); 
    };

    const handleCloseAndUnload = () => {
        // If recording is active, show special warning
        if (isRecording) {
            confirmDialog({
                header: null,
                message: (
                    <div className="dialog-message">
                        <i className="pi pi-exclamation-triangle gradient-icon" style={{ color: '#ff9800' }} />
                        <strong>Recording is in progress!</strong>
                        <p style={{ marginTop: '8px' }}>Please stop the recording before ending the call to ensure all data is saved.</p>
                    </div>
                ),
                icon: null,
                acceptLabel: 'Stop Recording & End Call',
                rejectLabel: 'Cancel',
                acceptClassName: 'custom-accept-btn',
                rejectClassName: 'custom-reject-btn',
                position: 'center',
                accept: () => {
                    // Close dialog immediately and show loader
                    console.log('🔴 Accept clicked, showing loader...');
                    setIsStoppingRecording(true);
                    
                    // Use setTimeout to ensure state updates
                    setTimeout(async () => {
                        console.log('⏳ Starting stop recording process...');
                        // Signal iframe to stop recording gracefully
                        if (iframeRef.current?.contentWindow) {
                            console.log('📨 Sending STOP_RECORDING message to iframe');
                            iframeRef.current.contentWindow.postMessage(
                                { type: 'STOP_RECORDING' },
                                '*'
                            );
                            
                            // Wait for recording to stop (with longer timeout for upload completion)
                            console.log('⏰ Waiting for recording to stop (5 seconds max)...');
                            await new Promise((resolve) => {
                                const timeout = setTimeout(() => {
                                    console.log('⏱️ Timeout reached after 5 seconds');
                                    resolve(true);
                                }, 5000);
                                const handler = (e: MessageEvent) => {
                                    if (e.data.type === 'RECORDING_STOPPED') {
                                        console.log('✅ RECORDING_STOPPED received from iframe');
                                        clearTimeout(timeout);
                                        window.removeEventListener('message', handler);
                                        resolve(true);
                                    }
                                };
                                window.addEventListener('message', handler);
                            });
                        }
                        
                        // Now safe to close iframe
                        console.log('🧹 Cleaning up and closing iframe...');
                        setIsStoppingRecording(false);
                        setIsRecording(false);
                        dispatch(setChatAgtId(null));
                        setVisibleIframe(false);
                        setIframeLoaded(false); // This unmounts the iframe
                        setShowIcon(false);
                        console.log('✅ Cleanup complete');
                    }, 100);
                },
                reject: () => {
                    // Do nothing
                },
            });
        } else {
            // No recording active, show normal confirmation
            confirmDialog({
                header: null,
                message: (
                    <div className="dialog-message">
                        <i className="pi pi-exclamation-circle gradient-icon" />
                        <strong>Do you want to end the call?</strong>
                    </div>
                ),
                icon: null,
                acceptLabel: 'Yes, End Call',
                rejectLabel: 'Cancel',
                acceptClassName: 'custom-accept-btn',
                rejectClassName: 'custom-reject-btn',
                position: 'center',
                accept: () => {
                    // Close dialog immediately and show loader
                    setIsStoppingRecording(true);
                    
                    // Use setTimeout to ensure state updates
                    setTimeout(async () => {
                        // Signal iframe to stop recording gracefully (if any)
                        if (iframeRef.current?.contentWindow) {
                            iframeRef.current.contentWindow.postMessage(
                                { type: 'STOP_RECORDING' },
                                '*'
                            );
                            
                            // Wait for recording to stop (with timeout)
                            await new Promise((resolve) => {
                                const timeout = setTimeout(resolve, 2000); // 2 seconds for cleanup
                                const handler = (e: MessageEvent) => {
                                    if (e.data.type === 'RECORDING_STOPPED') {
                                        clearTimeout(timeout);
                                        window.removeEventListener('message', handler);
                                        resolve(true);
                                    }
                                };
                                window.addEventListener('message', handler);
                            });
                        }
                        
                        // Now safe to close iframe
                        setIsStoppingRecording(false);
                        dispatch(setChatAgtId(null));
                        setVisibleIframe(false);
                        setIframeLoaded(false); // This unmounts the iframe
                        setShowIcon(false);
                    }, 100);
                },
                reject: () => {
                    // Do nothing
                },
            });
        }
    };

    return (
        <>
            {/* Floating Icon */}
            {showIcon && (
                <button
                    type="button"
                    aria-label="Voice Call"
                    onMouseDown={handleMouseDown}
                    onClick={handleOpen}
                    style={{
                        position: 'fixed',
                        top: position.y,
                        left: position.x,
                        zIndex: 10000,
                        animation: 'pulse 2s infinite',
                        cursor: 'grab',
                        userSelect: 'none',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                    }}
                    title="Voice Call"
                />
            )}

            {/* Expanded Modal */}
            {iframeLoaded && (
                <div
                    style={{
                        display: visibleIframe ? 'flex' : 'none',
                        position: 'fixed',
                        top: 100,
                        left: 150,
                        width: '60vw',
                        height: '80vh',
                        // display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            width: '80%',
                            height: '80%',
                            backgroundColor: '#2D3E46',
                            position: 'relative',
                            borderRadius: '10px',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Decline / Offline banners */}
                        {agentDeclined && !agentAccepted && (
                            <div className="decline-banner">
                                <img src={notification} alt='notification' className='notification-img' />
                                The user declined the Call
                            </div>
                        )}
                        {agentOffline && !agentAccepted && (
                            <div className="offline-banner">
                                <img src={notification} alt="notification" className='notification-img' />
                                This user isn&apos;t currently online
                            </div>
                        )}

                        {/* Stopping Recording Overlay */}
                        {isStoppingRecording && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 2000,
                                gap: '16px'
                            }}>
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem', color: '#fff' }} />
                                <div style={{ color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                                    {isRecording ? 'Stopping recording and saving data...' : 'Ending call...'}
                                </div>
                                <div style={{ color: '#ccc', fontSize: '14px' }}>
                                    Please wait, do not close this window
                                </div>
                            </div>
                        )}

                        {/* Controls */}
                        <div className="call-controls">
                            <Button
                                icon="pi pi-window-minimize"
                                className="p-button-sm p-button-text minimize-button"
                                onClick={handleMinimize}
                                tooltip="Minimise Dialog"
                            />
                            <button
                                type="button"
                                className="end-call-btn"
                                aria-label="Close & End the Call"
                                onClick={handleCloseAndUnload}
                            >
                                <img src={drop} alt="" aria-hidden="true" />
                            </button>
                        </div>

                        {/* Iframe */}
                        <iframe
                            ref={iframeRef}
                            src={src}
                            title="Persistent Iframe"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                            }}
                            // Audio-only: camera and screen sharing removed
                            allow="microphone *; fullscreen; clipboard-read; clipboard-write; web-share; autoplay"
                        />
                    </div>
                </div>
            )}

            {/* Minimized Floating Views — driven by minimizedVariant (only one renders) */}
            {iframeLoaded && isMinimized && minimizedVariant === 'two' && (
                <div
                    role="button"
                    tabIndex={0}
                    onMouseDown={handleMouseDown}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleRestore(); 
                        }
                    }}
                    style={{
                        position: "fixed",
                        top: position.y,
                        left: position.x,
                        width: "280px",
                        height: "470px",
                        backgroundColor: "#2D3E46",
                        borderRadius: "16px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                        zIndex: 1000,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        cursor: "pointer",
                    }}
                >                    {/* Top bar */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '6px 8px', gap: '8px' }}>
                        <Button
                            icon="pi pi-window-maximize"
                            className="p-button-rounded p-button-text p-button-sm"
                            onClick={handleRestore}
                            tooltip="Restore"
                            style={{ background: "transparent", border: "none", color: "#fff" }}
                        />
                        <button type="button" onClick={handleCloseAndUnload} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: "16px", color: "#fff" }}>
                            ✕
                        </button>
                    </div>

                    {/* Two Avatars */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "24px", padding: "20px" }}>
                        <div style={{ width: '100%', backgroundColor: '#3F5765', borderRadius: '32px', padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Avatar name={currentUserName} size={90} />
                        </div>
                        <div style={{ width: '100%', backgroundColor: '#3F5765', borderRadius: '32px', padding: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Avatar name={otherUserName} size={90} />
                        </div>
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            padding: '16px 20px 16px 20px',
                            borderTop: '1px solid rgba(255,255,255,0.2)',
                            display: 'flex',
                            justifyContent: 'center', 
                        }}
                    >
                        <Button
                            label="Hang Up"
                            icon="pi pi-phone"
                            className="p-button-rounded"
                            style={{
                                backgroundColor: '#E53935',
                                border: 'none',
                                padding: '10px 20px', 
                            }}
                            onClick={handleCloseAndUnload}
                        />
                    </div>
                </div>
            )}

            {iframeLoaded && isMinimized && minimizedVariant === 'single' && (
                <div
                    role="button"
                    tabIndex={0}
                    onMouseDown={handleMouseDown}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleRestore();
                        }
                    }}
                    style={{
                        position: 'fixed',
                        top: position.y,
                        left: position.x,
                        width: '280px',
                        height: '440px',
                        backgroundColor: '#2D3E46',
                        borderRadius: '16px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        cursor: 'pointer',
                    }}
                >
                    {/* Top bar */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 8px', gap: '8px' }}>
                        <Button
                            icon="pi pi-window-maximize"
                            className="p-button-rounded p-button-text p-button-sm"
                            onClick={handleRestore}
                            tooltip="Restore"
                            style={{ background: "transparent", border: "none", color: "#fff" }}
                        />
                        <button
                            type="button"
                            onClick={handleCloseAndUnload}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "16px",
                                color: "#fff",
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Content */}
                    <div
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "12px",
                            padding: "16px",
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                backgroundColor: "#3F5765",
                                borderRadius: "24px",
                                padding: "32px 10px",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <Avatar
                                name={agentDeclined || agentOffline ? currentUserName : otherUserName}
                                size={100}
                            />
                        </div>

                        {/* Conditional dots */}
                        {!agentDeclined && !agentOffline ? (
                            <div className="dots-loader" style={{ marginTop: "12px", marginBottom: "4px" }}>
                                <span /><span /><span />
                            </div>
                        ) : (
                            <div style={{ marginTop: "12px", marginBottom: "4px", display: 'flex', gap: '6px' }}>
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff', opacity: 0.5 }} />
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff', opacity: 0.5 }} />
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff', opacity: 0.5 }} />
                            </div>
                        )}

                        <p style={{ color: "#fff", fontSize: 18, fontWeight: 800, margin: 0, textAlign: 'center' }}>
                            {(agentDeclined || agentOffline) && agentAccepted
                                ? "The call has ended."
                                : "Waiting for users to join"}
                        </p>
                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            padding: "16px 20px 16px 20px",
                            borderTop: "1px solid rgba(255,255,255,0.2)",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Button
                            label="Hang Up"
                            icon="pi pi-phone"
                            className="p-button-rounded"
                            style={{
                                backgroundColor: "#E53935",
                                border: "none",
                                padding: "10px 20px", 
                            }}
                            onClick={handleCloseAndUnload}
                        />
                    </div>

                </div>
            )}
            
            {/* Confirmation Dialog */}
            <ConfirmDialog />
            <Dialog
                visible={showStartRecordingModal}
                onHide={() => setShowStartRecordingModal(false)}
                modal
                showHeader={false}
                closable={false}
                style={{ width: '500px', borderRadius: '16px', overflow: 'hidden' }}
                contentStyle={{ textAlign: 'center', padding: '0', borderRadius: '16px' }}
            >
                <div style={{ padding: '30px 20px 20px' }}>
                    <div
                        style={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            margin: '0 auto 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 32,
                            background: 'linear-gradient(90deg, #EE69DF 0%, #7D71E2 100%)',
                            fontWeight: 'bold',
                        }}
                    >
                        <img src={warning} alt="Warning" />
                    </div>
                    <h3 style={{ marginBottom: 12, color: '#7D71E2', fontSize: '24px', fontWeight: 'extra-bold', margin: '0 0 12px 0' }}>
                        Start Recording
                    </h3>
                    <p style={{ font: 'Urbanist', fontSize: 18, fontStyle: 'italic', color: '#C90370', marginBottom: 24, fontWeight: 700, lineHeight: '1.4' }}>
                        This recording will only be available for 14 days from the date of creation.
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                        <Button
                            label="Close"
                            className="p-button-outlined p-button-rounded"
                            style={{
                                color: '#D81B60',
                                borderColor: '#D81B60',
                                fontWeight: 600,
                                padding: '10px 24px'
                            }}
                            onClick={() => setShowStartRecordingModal(false)}
                        />
                        <Button
                            label="Continue"
                            className="p-button-rounded"
                            style={{
                                background: 'linear-gradient(90deg, #F069DE 0%, #6B6FF3 100%)',
                                border: 'none',
                                fontWeight: 600,
                                padding: '10px 24px'
                            }}
                            onClick={() => {
                                setShowStartRecordingModal(false);
                                iframeRef.current?.contentWindow?.postMessage(
                                    { type: 'START_RECORDING' },
                                    '*'
                                );
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <Dialog
                visible={showRecordingEndedModal}
                onHide={() => setShowRecordingEndedModal(false)}
                modal
                showHeader={false}
                closable={false}
                style={{ width: '500px', borderRadius: '16px', overflow: 'hidden' }}
                contentStyle={{ textAlign: 'center', padding: '0', borderRadius: '16px' }}
            >
                <div style={{ padding: '30px 20px 20px' }}>
                    <div
                        style={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            margin: '0 auto 16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 32,
                            background: 'linear-gradient(90deg, #EE69DF 0%, #7D71E2 100%)',
                            fontWeight: 'bold',
                            color: '#fff'
                        }}
                    >
                        <img src={warning} alt="Warning" />
                    </div>
                    <h3 style={{ font: 'Urbanist', marginBottom: 12, color: '#7D71E2', fontSize: '24px', fontWeight: 'extra-bold', margin: '0 0 12px 0' }}>
                        Recording Ended
                    </h3>
                    <p style={{ fontSize: 16, color: '#404D61', marginBottom: 12, fontWeight: 'bold' }}>
                        Do you want to navigate to the recording library?
                    </p>
                    <p style={{ font: 'Urbanist', fontWeight: 500, fontStyle: 'Medium', lineHeight: '100%', letterSpacing: '0%', color: '#404D61', marginBottom: 24 }}>
                        Note: This recording may take some time to process
                    </p>
                    <p style={{ fontSize: 14, fontStyle: 'italic', color: '#C90370', marginBottom: 24, fontWeight: 700, lineHeight: '1.4' }}>
                        Note: This recording will only be available for 14 days from the date of creation
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                        <Button
                            label="Close"
                            className="p-button-outlined p-button-rounded"
                            style={{
                                color: '#D81B60',
                                borderColor: '#D81B60',
                                fontWeight: 600,
                                padding: '10px 24px'
                            }}
                            onClick={() => setShowRecordingEndedModal(false)}
                        />
                        <Button
                            label="Continue"
                            className="p-button-rounded"
                            style={{
                                background: 'linear-gradient(90deg, #F069DE 0%, #6B6FF3 100%)',
                                border: 'none',
                                fontWeight: 600,
                                padding: '10px 24px'
                            }}
                            onClick={() => {
                                setShowRecordingEndedModal(false);
                                navigate(`${COACHING}/${CoachingTabs.RecordingHistory}`);
                            }}
                        />
                    </div>
                </div>
            </Dialog>
            <UserConsentDialog
                visible={visible}
                onAccept={handleAccept}
                onReject={handleReject}
                description="By joining this call, you consent to be recorded. 
                      Do you want to proceed?"
            />
            {/* Button Animation */}
            <style>
                {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
        `}
            </style>
        </>
    );
};

export default PersistentIframeModal;
